import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

const DowntimePage = () => {
  const [isGameActive, setIsGameActive] = useState(false);
  const [isJumping, setIsJumping] = useState(false);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [obstaclePosition, setObstaclePosition] = useState(100);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes = 600 seconds
  const gameLoopRef = useRef(null);
  const timerRef = useRef(null);

  // Start game
  const startGame = () => {
    setIsGameActive(true);
    setGameOver(false);
    setScore(0);
    setObstaclePosition(100);
    setIsJumping(false);
    setTimeLeft(600); // Reset timer to 10 minutes
  };

  // Handle jump
  const jump = () => {
    if (!isJumping && !gameOver) {
      setIsJumping(true);
      setTimeout(() => setIsJumping(false), 500);
    }
  };

  // Timer countdown
  useEffect(() => {
    if (isGameActive && !gameOver) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setGameOver(true);
            clearInterval(gameLoopRef.current);
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timerRef.current);
    }
  }, [isGameActive, gameOver]);

  // Game loop
  useEffect(() => {
    if (isGameActive && !gameOver) {
      gameLoopRef.current = setInterval(() => {
        setObstaclePosition((prev) => {
          if (prev <= 0) {
            setScore((s) => s + 1);
            return 100;
          }
          return prev - 2;
        });
      }, 20);

      return () => clearInterval(gameLoopRef.current);
    }
  }, [isGameActive, gameOver]);

  // Collision detection
  useEffect(() => {
    if (isGameActive && !gameOver) {
      const obstacleLeft = obstaclePosition;
      const playerBottom = isJumping ? 150 : 50;

      // Collision happens when obstacle is in player zone and player is not jumping high enough
      if (obstacleLeft > 5 && obstacleLeft < 18 && playerBottom < 90) {
        setGameOver(true);
        clearInterval(gameLoopRef.current);
      }
    }
  }, [obstaclePosition, isJumping, isGameActive, gameOver]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === "Space" && isGameActive && !gameOver && !isJumping) {
        e.preventDefault();
        setIsJumping(true);
        setTimeout(() => setIsJumping(false), 500);
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isGameActive, isJumping, gameOver]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Container>
      <ContentBox>
        <ContentWrapper>
          <IconWrapper>
            <MaintenanceIcon>
              <svg
                width="80"
                height="80"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                  fill="#f6591d"
                />
              </svg>
            </MaintenanceIcon>
          </IconWrapper>

          <Title>We'll Be Right Back</Title>

          <Description>
            Our system is currently undergoing scheduled maintenance. We
            apologize for any inconvenience and appreciate your patience.
          </Description>

          {!isGameActive ? (
            <ButtonGroup>
              <GameButton onClick={startGame}>
                🎮 Play Game While You Wait
              </GameButton>
              <RefreshButton onClick={() => window.location.reload()}>
                Refresh Page
              </RefreshButton>
            </ButtonGroup>
          ) : (
            <GameContainer>
              <GameHeader>
                <ScoreBoard>Score: {score}</ScoreBoard>
                <TimerBoard timeWarning={timeLeft <= 60}>
                  Time: {formatTime(timeLeft)}
                </TimerBoard>
              </GameHeader>
              <GameArea onClick={jump}>
                <Player isJumping={isJumping}>🟠</Player>
                <Obstacle position={obstaclePosition}>🔶</Obstacle>
                <Ground />
                {gameOver && (
                  <GameOverlay>
                    <GameOverText>
                      {timeLeft === 0 ? "Time's Up!" : "Game Over!"}
                    </GameOverText>
                    <GameOverScore>Final Score: {score}</GameOverScore>
                    {timeLeft === 0 && (
                      <GameOverMessage>
                        Amazing! You survived for 10 minutes! 🎉
                      </GameOverMessage>
                    )}
                    <GameOverButtonGroup>
                      <GameButton onClick={startGame}>Play Again</GameButton>
                      <BackButton onClick={() => setIsGameActive(false)}>
                        Back to Home
                      </BackButton>
                    </GameOverButtonGroup>
                  </GameOverlay>
                )}
              </GameArea>
              <GameInstructions>
                Press SPACE or Click to Jump
              </GameInstructions>
            </GameContainer>
          )}
        </ContentWrapper>
      </ContentBox>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #000000 0%, #0d0d0d 40%, rgba(246, 89, 29, 0.3) 100%);
  padding: 20px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999;
`;

const ContentBox = styled.div`
  background: #2d2d2d;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  padding: 5rem;
  max-width: 1000px;
  width: 100%;
  border: 1px solid #404040;

  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 12px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  text-align: center;
`;

const IconWrapper = styled.div`
  margin-bottom: 1.5rem;
`;

const MaintenanceIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 90px;
  height: 90px;
  background: rgba(246, 89, 29, 0.1);
  border-radius: 50%;
  border: 2px solid #f6591d;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.75rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const Description = styled.p`
  font-size: 1rem;
  color: #b0b0b0;
  margin-bottom: 2rem;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  margin-top: 1rem;
`;

const RefreshButton = styled.button`
  background: #f6591d;
  color: #ffffff;
  border: none;
  padding: 0.875rem 1.75rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(246, 89, 29, 0.2);

  &:hover {
    background: #e04f19;
    box-shadow: 0 4px 12px rgba(246, 89, 29, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const GameButton = styled.button`
  background: #f6591d;
  color: #ffffff;
  border: none;
  padding: 0.875rem 1.75rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(246, 89, 29, 0.2);

  &:hover {
    background: #e04f19;
    box-shadow: 0 4px 12px rgba(246, 89, 29, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const GameContainer = styled.div`
  margin-top: 2rem;
  width: 100%;
  position: relative;
`;

const GameHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const ScoreBoard = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #f6591d;
  text-align: center;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const TimerBoard = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.timeWarning ? '#ff6b6b' : '#e0e0e0'};
  text-align: center;
  flex: 1;
  animation: ${props => props.timeWarning ? 'pulse 1s infinite' : 'none'};

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const GameArea = styled.div`
  position: relative;
  width: 100%;
  height: 350px;
  background: #1a1a1a;
  border: 2px solid #404040;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  user-select: none;

  @media (max-width: 768px) {
    height: 280px;
  }
`;

const Player = styled.div`
  position: absolute;
  left: 70px;
  bottom: ${(props) => (props.isJumping ? "150px" : "50px")};
  font-size: 3.5rem;
  transition: bottom 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;

  @media (max-width: 768px) {
    left: 50px;
    font-size: 2.75rem;
    bottom: ${(props) => (props.isJumping ? "110px" : "40px")};
  }
`;

const Obstacle = styled.div`
  position: absolute;
  left: ${(props) => props.position}%;
  bottom: 50px;
  font-size: 3rem;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 2.25rem;
    bottom: 40px;
  }
`;

const Ground = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50px;
  background: #404040;
  border-top: 2px solid #606060;

  @media (max-width: 768px) {
    height: 40px;
  }
`;

const GameOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #2d2d2d;
  padding: 2.5rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  border: 1px solid #404040;
  z-index: 10;
  min-width: 320px;

  @media (max-width: 768px) {
    padding: 2rem;
    min-width: 280px;
  }
`;

const GameOverText = styled.h2`
  font-size: 1.75rem;
  color: #ffffff;
  font-weight: 600;
  margin-bottom: 0.75rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const GameOverScore = styled.p`
  font-size: 1.5rem;
  color: #f6591d;
  font-weight: 600;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const GameOverMessage = styled.p`
  font-size: 1rem;
  color: #4ade80;
  font-weight: 500;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const BackButton = styled.button`
  background: #3d3d3d;
  color: #b0b0b0;
  border: 1px solid #505050;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #4a4a4a;
    border-color: #606060;
    color: #e0e0e0;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const GameOverButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const GameInstructions = styled.p`
  text-align: center;
  margin-top: 1rem;
  font-size: 0.9rem;
  color: #909090;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

export default DowntimePage;
